import { SalesPage } from './sales';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError, api, download } from '../../lib/api';
import { homeFor, type Membership, useAuth } from '../../lib/auth';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { Link, navigate, useLocation } from '../../lib/router';
import { AsyncButton, Badge, Button, Card, Checkbox, CopyField, DataTable, errorText, Field, fieldErrors, Input, Loading, Modal, PageHeader, Select, useToast } from '../../ui/components';
import { Centered, LanguageSelect } from '../../ui/layout';

export function goHome(tenants: Membership[], opts: { next?: string | null; gym?: string | null; platform?: boolean } = {}): void {
  if (opts.next && opts.next.startsWith('/') && !opts.next.startsWith('//')) { navigate(opts.next, { replace: true }); return; }
  const m = tenants.find((t) => t.slug === opts.gym) ?? tenants[0];
  if (m) navigate(homeFor(m), { replace: true });
  else navigate(opts.platform ? '/platform' : '/account', { replace: true });
}

export function HomeRedirect() {
  const { user, tenants } = useAuth();
  useEffect(() => {
    if (user) goHome(tenants, { platform: user.isPlatformAdmin });
  }, [user]);
  // Someone who is not logged in is most likely a gym owner looking around: show them what this is.
  return user ? <Loading /> : <SalesPage />;
}

export function LoginPage() {
  const { login, user, tenants } = useAuth();
  const i18n = useI18n();
  const { query } = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (user) goHome(tenants, { next: query.get('next'), gym: query.get('gym'), platform: user.isPlatformAdmin }); }, [user]);
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(email, password, needTotp ? totp : undefined);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'mfa_required') setNeedTotp(true);
      else setError(errorText(err, i18n));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Centered>
      <h1>{i18n.t('auth.login_title')}</h1>
      <form className="stack" onSubmit={submit}>
        <Field label={i18n.field('email')}><Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label={i18n.t('auth.password')}><Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {needTotp && <Field label={i18n.t('auth.totp_code')}><Input inputMode="numeric" autoComplete="one-time-code" autoFocus value={totp} onChange={(e) => setTotp(e.target.value)} /></Field>}
        {error && <div className="alert alert-error">{error}</div>}
        <Button type="submit" size="lg" busy={busy}>{i18n.t('auth.login')}</Button>
      </form>
      <div className="row between wrap small">
        <Link to="/forgot-password">{i18n.t('auth.forgot')}</Link>
        <Link to="/start">{i18n.t('auth.start_gym')}</Link>
      </div>
    </Centered>
  );
}

export function ForgotPasswordPage() {
  const i18n = useI18n();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  return (
    <Centered>
      <h1>{i18n.t('auth.forgot_title')}</h1>
      {sent ? <p className="alert alert-ok">{i18n.t('auth.forgot_sent')}</p> : (
        <form className="stack" onSubmit={(e) => e.preventDefault()}>
          <p className="muted">{i18n.t('auth.forgot_help')}</p>
          <Field label={i18n.field('email')}><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <AsyncButton size="lg" onClick={async () => { await api('/v1/auth/forgot-password', { body: { email } }); setSent(true); }}>{i18n.t('auth.send_link')}</AsyncButton>
        </form>
      )}
      <Link to="/login" className="small">{i18n.t('auth.back_to_login')}</Link>
    </Centered>
  );
}

/** Handles both /reset-password and /accept-invite links. */
export function SetPasswordPage({ mode }: { mode: 'reset' | 'invite' }) {
  const i18n = useI18n();
  const { startSession } = useAuth();
  const { query } = useLocation();
  const toast = useToast();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [done, setDone] = useState(false);
  const token = query.get('token') ?? '';
  const submit = async (): Promise<void> => {
    if (pw !== pw2) throw new Error(i18n.t('auth.passwords_differ'));
    if (mode === 'reset') {
      await api('/v1/auth/reset-password', { body: { token, password: pw } });
      setDone(true);
    } else {
      const r = await api('/v1/auth/accept-invite', { body: { token, password: pw } });
      await startSession(r);
      toast.ok(i18n.t('auth.welcome'));
      goHome(r.tenants ?? [], { gym: query.get('gym') });
    }
  };
  return (
    <Centered>
      <h1>{i18n.t(mode === 'reset' ? 'auth.reset_title' : 'auth.invite_title')}</h1>
      {done ? (
        <><p className="alert alert-ok">{i18n.t('auth.reset_done')}</p><Link to="/login">{i18n.t('auth.login')}</Link></>
      ) : (
        <form className="stack" onSubmit={(e) => e.preventDefault()}>
          <Field label={i18n.t('auth.new_password')} hint={i18n.t('auth.password_rules')}><Input type="password" autoComplete="new-password" minLength={10} value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
          <Field label={i18n.t('auth.repeat_password')}><Input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></Field>
          <AsyncButton size="lg" disabled={!token || pw.length < 10} onClick={submit}>{i18n.t('common.save')}</AsyncButton>
        </form>
      )}
    </Centered>
  );
}

export function VerifyEmailPage() {
  const i18n = useI18n();
  const { query } = useLocation();
  const state = useApi(() => api('/v1/auth/verify-email', { body: { token: query.get('token') ?? '' } }), []);
  return (
    <Centered>
      <h1>{i18n.t('auth.verify_title')}</h1>
      {state.loading ? <Loading /> : state.error ? <p className="alert alert-error">{i18n.t('auth.verify_failed')}</p> : <p className="alert alert-ok">{i18n.t('auth.verify_done')}</p>}
      <Link to="/">{i18n.t('common.continue')}</Link>
    </Centered>
  );
}

export const COUNTRIES = ['AD', 'AL', 'AT', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GB', 'GR', 'HR', 'HU', 'IE', 'IS', 'IT', 'LI', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS', 'SE', 'SI', 'SK', 'SM', 'TR', 'UA', 'XK',
  'AG', 'AR', 'AW', 'BB', 'BO', 'BR', 'BS', 'BZ', 'CA', 'CL', 'CO', 'CR', 'CU', 'CW', 'DM', 'DO', 'EC', 'GD', 'GL', 'GT', 'GY', 'HN', 'HT', 'JM', 'KN', 'LC', 'MX', 'NI', 'PA', 'PE', 'PR', 'PY', 'SR', 'SV', 'TT', 'US', 'UY', 'VC', 'VE'];

export function useCountryOptions(): { value: string; label: string }[] {
  const i18n = useI18n();
  return useMemo(() => {
    let names: Intl.DisplayNames | null = null;
    try { names = new Intl.DisplayNames([i18n.locale], { type: 'region' }); } catch { /* old browser */ }
    return COUNTRIES.map((c) => ({ value: c, label: names?.of(c) ?? c })).sort((a, b) => a.label.localeCompare(b.label, i18n.locale));
  }, [i18n.locale]);
}

export function RegisterGymPage() {
  const i18n = useI18n();
  const { user, startSession } = useAuth();
  const countries = useCountryOptions();
  const toast = useToast();
  const { query } = useLocation();
  const signupToken = query.get('invite') ?? '';
  const [v, setV] = useState({ gymName: '', country: 'NL', firstName: '', lastName: '', email: '', password: '', locationName: '', street: '', postalCode: '', city: '', acceptTerms: false, acceptDpa: false, plan: query.get('plan') ?? 'growth' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const set = (k: string, val: unknown): void => setV((s) => ({ ...s, [k]: val }));
  const [offer, setOffer] = useState<{ plans: { key: string; name: string; priceMonthCents: number | null }[]; trialDays: number } | null>(null);
  useEffect(() => { void api('/v1/public/plans').then(setOffer).catch(() => setOffer(null)); }, []);
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api('/v1/auth/register-gym', { body: { ...v, signupToken: signupToken || undefined, locale: i18n.locale, locationName: v.locationName || v.gymName } });
      await startSession(r);
      navigate(`/g/${r.tenant.slug}/admin/welcome`);
    } catch (err) {
      setErrors(fieldErrors(err));
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Centered wide>
      <h1>{i18n.t('start.title')}</h1>
      <p className="muted">{i18n.t('start.subtitle')}</p>
      {signupToken && <div className="alert alert-ok">{i18n.t('start.invited')}</div>}
      <form className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field label={i18n.t('start.gym_name')} error={errors.gymName}><Input required value={v.gymName} onChange={(e) => set('gymName', e.target.value)} /></Field>
          <Field label={i18n.field('country')}><Select value={v.country} onChange={(e) => set('country', e.target.value)} options={countries} /></Field>
          {!user && <>
            <Field label={i18n.field('first_name')}><Input required value={v.firstName} onChange={(e) => set('firstName', e.target.value)} /></Field>
            <Field label={i18n.field('last_name')}><Input required value={v.lastName} onChange={(e) => set('lastName', e.target.value)} /></Field>
            <Field label={i18n.field('email')} error={errors.email}><Input type="email" required autoComplete="username" value={v.email} onChange={(e) => set('email', e.target.value)} /></Field>
            <Field label={i18n.t('auth.password')} hint={i18n.t('auth.password_rules')} error={errors.password}><Input type="password" required minLength={10} autoComplete="new-password" value={v.password} onChange={(e) => set('password', e.target.value)} /></Field>
          </>}
          <Field label={i18n.t('start.location_name')} hint={i18n.t('start.location_hint')}><Input value={v.locationName} placeholder={v.gymName} onChange={(e) => set('locationName', e.target.value)} /></Field>
          <Field label={i18n.field('street')}><Input value={v.street} onChange={(e) => set('street', e.target.value)} /></Field>
          <Field label={i18n.field('postal_code')}><Input value={v.postalCode} onChange={(e) => set('postalCode', e.target.value)} /></Field>
          <Field label={i18n.field('city')}><Input value={v.city} onChange={(e) => set('city', e.target.value)} /></Field>
        </div>
        {offer && offer.plans.length > 0 && (
          <Field label={i18n.t('start.plan')} hint={i18n.t('start.plan_hint', { days: offer.trialDays })}>
            <Select value={v.plan} onChange={(e) => set('plan', e.target.value)}
              options={offer.plans.map((p) => ({ value: p.key, label: p.priceMonthCents ? `${p.name} · ${i18n.money(p.priceMonthCents)} ${i18n.t('start.per_month')}` : p.name }))} />
          </Field>
        )}
        <Checkbox label={<>{i18n.t('start.accept_terms_prefix')} <a href="/legal/terms" target="_blank" rel="noreferrer">{i18n.t('start.terms_link')}</a></>}
          checked={v.acceptTerms} onChange={(c) => set('acceptTerms', c)} />
        <Checkbox label={<>{i18n.t('start.accept_dpa_prefix')} <a href="/legal/dpa" target="_blank" rel="noreferrer">{i18n.t('start.dpa_link')}</a></>}
          hint={i18n.t('start.dpa_hint')} checked={v.acceptDpa} onChange={(c) => set('acceptDpa', c)} />
        <p className="small muted">{i18n.t('start.privacy_note')} <a href="/legal/privacy" target="_blank" rel="noreferrer">{i18n.t('start.privacy_link')}</a></p>
        <Button type="submit" size="lg" busy={busy} disabled={!v.acceptTerms || !v.acceptDpa}>{i18n.t('start.create')}</Button>
      </form>
      <Link to="/login" className="small">{i18n.t('auth.have_account')}</Link>
    </Centered>
  );
}

export function AccountPage() {
  const i18n = useI18n();
  const { user, tenants, reload, logout } = useAuth();
  const toast = useToast();
  const [name, setName] = useState(user?.name ?? '');
  const [pw, setPw] = useState({ current: '', next: '' });
  const [mfa, setMfa] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [mfaPw, setMfaPw] = useState('');
  const [delPw, setDelPw] = useState('');
  useEffect(() => { if (!user) navigate('/login?next=/account', { replace: true }); }, [user]);
  if (!user) return <Loading />;
  return (
    <div className="page narrow">
      <div className="row between"><Link to="/">← {i18n.t('common.back')}</Link><LanguageSelect /></div>
      <PageHeader title={i18n.t('account.title')} subtitle={user.email} />
      <Card title={i18n.t('account.my_gyms')}>
        <DataTable rows={tenants} columns={[
          { key: 'name', label: i18n.field('name'), render: (t) => <Link to={homeFor(t)}>{t.name}</Link> },
          { key: 'roles', label: i18n.t('account.roles'), render: (t) => <span className="row gap-sm wrap">{t.roles.map((r) => <Badge key={r} value="neutral" label={i18n.option(r)} />)}</span> },
        ]} empty={<>{i18n.t('account.no_gyms')} <Link to="/start">{i18n.t('auth.start_gym')}</Link></>} />
        {user.isPlatformAdmin && <p><Link to="/platform">{i18n.t('account.platform_admin')}</Link></p>}
      </Card>
      <Card title={i18n.t('account.profile')}>
        <div className="row gap-sm">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
          <AsyncButton variant="secondary" success={i18n.t('common.saved')} onClick={async () => { await api('/v1/me', { method: 'PATCH', body: { name } }); await reload(); }}>{i18n.t('common.save')}</AsyncButton>
        </div>
        {!user.emailVerified && <p className="small">{i18n.t('account.not_verified')} <AsyncButton variant="ghost" size="sm" success={i18n.t('account.verification_sent')} onClick={() => api('/v1/me/verify-email', { body: {} })}>{i18n.t('account.resend')}</AsyncButton></p>}
      </Card>
      <Card title={i18n.t('account.password')}>
        <div className="grid-2">
          <Field label={i18n.t('account.current_password')}><Input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} /></Field>
          <Field label={i18n.t('auth.new_password')} hint={i18n.t('auth.password_rules')}><Input type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} /></Field>
        </div>
        <AsyncButton variant="secondary" success={i18n.t('common.saved')} onClick={async () => { await api('/v1/me/password', { body: { currentPassword: pw.current, newPassword: pw.next } }); setPw({ current: '', next: '' }); }}>{i18n.t('account.change_password')}</AsyncButton>
      </Card>
      <Card title={i18n.t('account.mfa')}>
        {user.mfaEnabled ? (
          <div className="stack-sm">
            <p><Badge value="active" label={i18n.t('account.mfa_on')} /></p>
            <Field label={i18n.t('auth.password')}><Input type="password" value={mfaPw} onChange={(e) => setMfaPw(e.target.value)} /></Field>
            <AsyncButton variant="secondary" onClick={async () => { await api('/v1/me/mfa/disable', { body: { password: mfaPw } }); setMfaPw(''); await reload(); }}>{i18n.t('account.mfa_disable')}</AsyncButton>
          </div>
        ) : mfa ? (
          <div className="stack-sm">
            <p>{i18n.t('account.mfa_scan')}</p>
            <CopyField value={mfa.secret} />
            <p className="small"><a href={mfa.otpauthUrl}>{i18n.t('account.mfa_open_app')}</a></p>
            <Field label={i18n.t('auth.totp_code')}><Input inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} /></Field>
            <AsyncButton onClick={async () => { await api('/v1/me/mfa/enable', { body: { code } }); setMfa(null); await reload(); toast.ok(i18n.t('account.mfa_on')); }}>{i18n.t('account.mfa_confirm')}</AsyncButton>
          </div>
        ) : (
          <><p className="muted">{i18n.t('account.mfa_help')}</p><AsyncButton variant="secondary" onClick={async () => setMfa(await api('/v1/me/mfa/setup', { body: {} }))}>{i18n.t('account.mfa_setup')}</AsyncButton></>
        )}
      </Card>
      <Card title={i18n.t('account.delete')} className="danger-zone">
        <p className="muted">{i18n.t('account.delete_help')}</p>
        <div className="row gap-sm">
          <Input type="password" placeholder={i18n.t('auth.password')} value={delPw} onChange={(e) => setDelPw(e.target.value)} />
          <AsyncButton variant="danger" confirm={i18n.t('account.delete_confirm')} onClick={async () => {
            const r = await api('/v1/me/delete', { body: { password: delPw } });
            if (r.pendingAtGyms?.length) toast.info(i18n.t('account.delete_pending', { gyms: r.pendingAtGyms.join(', ') }));
            await logout();
          }}>{i18n.t('account.delete_button')}</AsyncButton>
        </div>
      </Card>
    </div>
  );
}

function AddGymModal({ onClose, onCreated }: { onClose(): void; onCreated(result: { inviteLink: string; ownerEmail: string; tenant: { slug: string; name: string } }): void }) {
  const i18n = useI18n();
  const countries = useCountryOptions();
  const [v, setV] = useState({ gymName: '', country: 'NL', firstName: '', lastName: '', ownerEmail: '', locationName: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: string, val: string): void => setV((s) => ({ ...s, [k]: val }));
  return (
    <Modal wide title={i18n.t('platform.add_gym')} onClose={onClose} footer={
      <AsyncButton disabled={!v.gymName || !v.ownerEmail} onClick={async () => {
        try {
          const r = await api('/v1/platform/tenants', { body: { ...v, locale: i18n.locale, locationName: v.locationName || v.gymName } });
          onCreated(r);
        } catch (e) { setErrors(fieldErrors(e)); throw e; }
      }}>{i18n.t('platform.create_gym')}</AsyncButton>
    }>
      <p className="small muted">{i18n.t('platform.add_gym_help')}</p>
      <div className="grid-2">
        <Field label={i18n.t('start.gym_name')} error={errors.gymName}><Input value={v.gymName} onChange={(e) => set('gymName', e.target.value)} /></Field>
        <Field label={i18n.field('country')}><Select value={v.country} onChange={(e) => set('country', e.target.value)} options={countries} /></Field>
        <Field label={i18n.field('first_name')}><Input value={v.firstName} onChange={(e) => set('firstName', e.target.value)} /></Field>
        <Field label={i18n.field('last_name')}><Input value={v.lastName} onChange={(e) => set('lastName', e.target.value)} /></Field>
        <Field label={i18n.t('platform.owner_email')} error={errors.ownerEmail} className="span-2"><Input type="email" value={v.ownerEmail} onChange={(e) => set('ownerEmail', e.target.value)} /></Field>
        <Field label={i18n.t('start.location_name')} className="span-2"><Input value={v.locationName} placeholder={v.gymName} onChange={(e) => set('locationName', e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

function InviteOwnerModal({ onClose, onCreated }: { onClose(): void; onCreated(result: { url: string }): void }) {
  const i18n = useI18n();
  const [v, setV] = useState({ email: '', name: '', note: '', days: 14 });
  return (
    <Modal title={i18n.t('platform.invite_owner')} onClose={onClose} footer={
      <AsyncButton onClick={async () => onCreated(await api('/v1/platform/signup-invites', { body: v }))}>{i18n.t('platform.create_link')}</AsyncButton>
    }>
      <p className="small muted">{i18n.t('platform.invite_owner_help')}</p>
      <div className="grid-2">
        <Field label={i18n.field('email')} hint={i18n.t('platform.invite_email_hint')}><Input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></Field>
        <Field label={i18n.t('platform.gym_or_contact')}><Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></Field>
        <Field label={i18n.t('platform.valid_days')}><Input type="number" min={1} max={90} value={v.days} onChange={(e) => setV({ ...v, days: Number(e.target.value) })} /></Field>
        <Field label={i18n.field('note')}><Input value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}

export function PlatformPage() {
  const i18n = useI18n();
  const { user } = useAuth();
  const stats = useApi(() => api<Record<string, number>>('/v1/platform/stats'), []);
  const tenants = useApi(() => api<any[]>('/v1/platform/tenants'), []);
  const invites = useApi(() => api<any[]>('/v1/platform/signup-invites'), []);
  const plans = useApi(() => api<any[]>('/v1/platform/plans'), []);
  const health = useApi(() => api<any>('/v1/platform/health'), []);
  const [adding, setAdding] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState<{ title: string; text: string; url: string } | null>(null);
  if (!user?.isPlatformAdmin) return <Centered><p>{i18n.t('error.forbidden')}</p></Centered>;
  const open = (invites.data ?? []).filter((i) => !i.used_at);
  return (
    <div className="page">
      <div className="row between"><Link to="/account">← {i18n.t('account.title')}</Link><LanguageSelect /></div>
      <PageHeader title={i18n.t('platform.title')} actions={<>
        <Button variant="secondary" onClick={() => setInviting(true)}>{i18n.t('platform.invite_owner')}</Button>
        <Button onClick={() => setAdding(true)}>+ {i18n.t('platform.add_gym')}</Button>
      </>} />
      {stats.data && (
        <div className="stats">
          {Object.entries(stats.data).map(([k, n]) => <div className="stat" key={k}><div className="stat-label">{i18n.t(`platform.${k}`)}</div><div className="stat-value">{i18n.num(n)}</div></div>)}
        </div>
      )}
      {health.data && !health.data.ok && (
        <Card title={i18n.t('platform.health')} className="danger-zone">
          <p className="warn">{health.data.problems.map((x: string) => i18n.t(`platform.problem_${x}`)).join(' · ')}</p>
          <p className="small muted">{i18n.t('platform.health_detail', {
            worker: health.data.worker.ageSeconds === null ? '—' : `${health.data.worker.ageSeconds}s`,
            jobs: health.data.jobs.queued, failed: health.data.jobs.failed, messages: health.data.notifications.failed,
          })}</p>
        </Card>
      )}
      <Card title={i18n.t('platform.gyms')}>
        {tenants.data ? <DataTable rows={tenants.data} columns={[
          { key: 'name', label: i18n.field('name'), render: (t) => <Link to={`/g/${t.slug}`}>{t.name}</Link> },
          { key: 'country', label: i18n.field('country') },
          { key: 'members', label: i18n.t('platform.members') },
          { key: 'created_at', label: i18n.field('created_at'), render: (t) => i18n.date(t.created_at) },
          { key: 'platform_status', label: i18n.t('platform.subscription'), render: (t) => <Badge value={t.platform_status} /> },
          { key: 'platform_plan', label: i18n.t('platform.plan'), render: (t) => (
            <Select value={t.platform_plan ?? ''} placeholder="—" options={(plans.data ?? []).map((p: any) => ({ value: p.key, label: p.name }))}
              onChange={async (e) => { await api(`/v1/platform/tenants/${t.id}/plan`, { method: 'PATCH', body: { plan: e.target.value } }); tenants.reload(); }} />
          ) },
          { key: 'data', label: '', render: (t) => (
            <div className="row gap-sm end">
              <Button size="sm" variant="ghost" onClick={() => download(`/v1/platform/tenants/${t.id}/export.json`, `${t.slug}-export.json`)}>{i18n.t('platform.export')}</Button>
              <AsyncButton size="sm" variant="danger" onClick={async () => {
                const typed = window.prompt(i18n.t('platform.delete_confirm', { slug: t.slug }));
                if (typed === null) return;
                await api(`/v1/platform/tenants/${t.id}`, { method: 'DELETE', body: { confirmSlug: typed } });
                tenants.reload();
              }}>{i18n.t('common.delete')}</AsyncButton>
            </div>
          ) },
          { key: 'status', label: i18n.field('status'), render: (t) => (
            <Select value={t.status} options={['trial', 'active', 'suspended', 'cancelled'].map((s) => ({ value: s, label: i18n.option(s) }))}
              onChange={async (e) => { await api(`/v1/platform/tenants/${t.id}`, { method: 'PATCH', body: { status: e.target.value } }); tenants.reload(); }} />
          ) },
        ]} /> : <Loading />}
      </Card>
      {open.length > 0 && (
        <Card title={i18n.t('platform.open_invites')}>
          <DataTable rows={open} columns={[
            { key: 'name', label: i18n.t('platform.gym_or_contact'), render: (i) => i.name ?? '—' },
            { key: 'email', label: i18n.field('email'), render: (i) => i.email ?? '—' },
            { key: 'note', label: i18n.field('note'), render: (i) => i.note ?? '—' },
            { key: 'expires_at', label: i18n.field('expires_at'), render: (i) => i.expires_at && i18n.date(i.expires_at) },
            { key: 'x', label: '', render: (i) => (
              <AsyncButton size="sm" variant="ghost" confirm={i18n.t('platform.withdraw_invite_confirm')}
                onClick={async () => { await api(`/v1/platform/signup-invites/${i.id}`, { method: 'DELETE' }); invites.reload(); }}>{i18n.t('platform.withdraw_invite')}</AsyncButton>
            ) },
          ]} />
        </Card>
      )}
      {adding && <AddGymModal onClose={() => setAdding(false)} onCreated={(r) => {
        setAdding(false);
        tenants.reload();
        stats.reload();
        setLink({ title: i18n.t('platform.gym_created', { gym: r.tenant.name }), text: i18n.t('platform.owner_invite_sent', { email: r.ownerEmail }), url: r.inviteLink });
      }} />}
      {inviting && <InviteOwnerModal onClose={() => setInviting(false)} onCreated={(r) => {
        setInviting(false);
        invites.reload();
        setLink({ title: i18n.t('platform.invite_created'), text: i18n.t('platform.invite_created_help'), url: r.url });
      }} />}
      {link && (
        <Modal title={link.title} onClose={() => setLink(null)} footer={<Button onClick={() => setLink(null)}>{i18n.t('common.close')}</Button>}>
          <p>{link.text}</p>
          <CopyField value={link.url} />
          <p className="small muted">{i18n.t('platform.link_help')}</p>
        </Modal>
      )}
    </div>
  );
}
